"use client";

import { Calendar, ChevronRight } from "lucide-react";
import Link from "next/link";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { BlogCardReadMoreArrow } from "@/components/molecules/BlogCardReadMoreArrow";
import { publicAxios } from "@/lib/axios";
import { cn } from "@/lib/utils";
import type { IAuthorBlogItem, IPageAuthor } from "@/types/author";

type BlogTab = "published" | "popular";

interface AuthorProfile1Props {
	data?: any;
	/** Page-level author injected by NewPage; used when the widget has no author of its own. */
	author?: IPageAuthor | null;
	isCmsPreview?: boolean;
}

/** Both tabs show 9 to start; Load More reveals/fetches 9 more each press. */
const PAGE_SIZE = 9;

const isTruthy = (v: unknown) => v === true || v === 1 || v === "1";

/**
 * Icons exported from Atlas and served locally, so a badge/social renders correctly
 * out of the box. An icon uploaded in the admin still wins over these.
 */
const DEFAULT_BADGE_ICONS = {
	top: "/svg/badge-top-contributor.png",
	month: "/svg/badge-author-of-month.png",
};

const DEFAULT_SOCIAL_ICONS: Record<string, string> = {
	linkedin: "/svg/linkedin.png",
	quora: "/svg/quora.png",
	twitter: "/svg/twitter.png",
};

/** Expertise / tools / certifications are stored as comma-separated free text. */
function splitList(raw?: string | null): string[] {
	return String(raw ?? "")
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);
}

const BLOG_TABS: { key: BlogTab; labelKey: BlogTab }[] = [
	{ key: "published", labelKey: "published" },
	{ key: "popular", labelKey: "popular" },
];

/** Figma: "30 Aug 2018" under the badges. */
function formatAssociatedDate(raw?: string | null): string {
	if (!raw) return "";
	const d = new Date(String(raw).length <= 10 ? `${raw}T00:00:00` : raw);
	if (Number.isNaN(d.getTime())) return "";
	return d.toLocaleDateString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
}

/** Figma blog card: "Sep 12th, 2024". */
function formatBlogDate(raw?: string | null): string {
	if (!raw) return "";
	const d = new Date(raw);
	if (Number.isNaN(d.getTime())) return "";
	return d.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function Badge({
	label,
	iconUrl,
	textColor,
	borderColor,
	backgroundColor,
}: {
	label: string;
	iconUrl?: string | null;
	textColor: string;
	borderColor: string;
	backgroundColor: string;
}) {
	return (
		<span
			className="inline-flex shrink-0 items-center justify-center gap-1 rounded-[40px] border-[0.5px] border-solid px-2.5 py-1.5"
			style={{ borderColor, backgroundColor }}
		>
			{iconUrl ? (
				// eslint-disable-next-line @next/next/no-img-element
				<img src={iconUrl} alt="" className="size-4 shrink-0 object-contain" />
			) : null}
			<span
				className="whitespace-nowrap text-[12px] leading-[1.4] tracking-[0.12px] sm:text-[14px] sm:tracking-[0.14px]"
				style={{ color: textColor }}
			>
				{label}
			</span>
		</span>
	);
}

function SocialLink({
	href,
	iconUrl,
	label,
	backgroundColor,
}: {
	href: string;
	iconUrl?: string | null;
	label: string;
	backgroundColor: string;
}) {
	return (
		<a
			href={href}
			target="_blank"
			// `nofollow` keeps the author's off-site profiles from absorbing this page's
			// link equity. ("noindex" is a page-level robots directive with no meaning on
			// an <a>; nofollow is its link-level counterpart.)
			rel="nofollow noopener noreferrer"
			aria-label={label}
			className="inline-flex items-center rounded-[10px] p-1 transition-opacity hover:opacity-80"
			style={{ backgroundColor }}
		>
			{iconUrl ? (
				// eslint-disable-next-line @next/next/no-img-element
				<img src={iconUrl} alt="" className="size-[18px] object-contain" />
			) : (
				<span className="text-[12px] font-medium underline">{label}</span>
			)}
		</a>
	);
}

/** One label + wrapped pills row (Expertise / Tools & Technologies / Certifications). */
function ChipGroup({
	label,
	items,
	labelColor,
	chipBackgroundColor,
	chipTextColor,
}: {
	label: string;
	items: string[];
	labelColor: string;
	chipBackgroundColor: string;
	chipTextColor: string;
}) {
	if (!items.length) return null;
	return (
		<div className="flex w-full flex-col gap-1">
			<p
				className="text-[12px] leading-[1.49] [word-break:break-word] sm:text-[14px]"
				style={{ color: labelColor }}
			>
				{label}
			</p>
			<div className="flex flex-wrap content-start items-start gap-2">
				{items.map((item) => (
					<span
						key={item}
						className="inline-flex shrink-0 items-center justify-center rounded-[6px] px-2 py-[5px] text-[10px] font-medium leading-[1.49] [word-break:break-word] sm:text-[12px]"
						style={{
							backgroundColor: chipBackgroundColor,
							color: chipTextColor,
						}}
					>
						{item}
					</span>
				))}
			</div>
		</div>
	);
}

/** One label + value row (Domain / Current role / Education Qualification). */
function InfoRow({
	label,
	value,
	labelColor,
	valueColor,
}: {
	label: string;
	value?: string | null;
	labelColor: string;
	valueColor: string;
}) {
	if (!value) return null;
	return (
		<div className="flex flex-col gap-0.5 leading-[1.49] [word-break:break-word] sm:gap-1">
			<p className="text-[12px] sm:text-[14px]" style={{ color: labelColor }}>
				{label}
			</p>
			<p
				className="text-[14px] font-medium sm:text-[16px]"
				style={{ color: valueColor }}
			>
				{value}
			</p>
		</div>
	);
}

/**
 * Author Profile 1 (section 3002 / variant 3000) — Atlas 13117:23393 (desktop)
 * and 13138:649 (mobile). Renders everything between the header and the footer:
 * breadcrumb, author card, About column, and the author's Published / Most
 * Popular blog list with Load More.
 *
 * Author content is never stored on the widget: the server joins the selected
 * author (`data.author`) so editing the author updates every page using it.
 */
export default function AuthorProfile1({
	data,
	author: pageAuthor,
	isCmsPreview,
}: AuthorProfile1Props) {
	const sectionData = data?.data ?? data;

	// Author resolution, in priority order:
	//   1. joined by the server on the widget row (`data.author`) — public + admin
	//   2. the page-level linked author injected by NewPage
	//   3. fetched here from `author_id` (covers the CMS preview, where the widget
	//      row is unsaved/not yet re-fetched)
	const joinedAuthor: IPageAuthor | null =
		sectionData?.author ?? pageAuthor ?? null;
	const configuredAuthorId = sectionData?.author_id;
	const [fetchedAuthor, setFetchedAuthor] = useState<IPageAuthor | null>(null);

	useEffect(() => {
		const id = Number(configuredAuthorId);
		if (joinedAuthor || !Number.isFinite(id) || id <= 0) {
			setFetchedAuthor(null);
			return;
		}
		let cancelled = false;
		publicAxios
			.get(`/author/public/${id}`)
			.then((res) => {
				if (cancelled) return;
				setFetchedAuthor(
					(res.data?.data ?? res.data ?? null) as IPageAuthor | null,
				);
			})
			.catch((e) => console.error(e));
		return () => {
			cancelled = true;
		};
	}, [configuredAuthorId, joinedAuthor]);

	const author: IPageAuthor | null = useMemo(
		() => joinedAuthor ?? fetchedAuthor,
		[joinedAuthor, fetchedAuthor],
	);

	const seededBlogs = sectionData?.blogs;

	const [tab, setTab] = useState<BlogTab>("published");
	/**
	 * One piece of state, because the three values are one fact: which blogs are
	 * loaded, how many exist, and how many of them are on screen. They used to be
	 * separate `useState`s that could drift — the seeding effect below reset the
	 * list without resetting the counter — so the list would collapse back to the
	 * first batch while the widget still believed two batches were showing.
	 */
	const [blogs, setBlogs] = useState<{
		items: IAuthorBlogItem[];
		total: number;
		visible: number;
	}>({ items: [], total: 0, visible: PAGE_SIZE });
	const [loading, setLoading] = useState(false);

	const authorId = author?.id ?? null;

	const fetchPage = useCallback(
		async (nextTab: BlogTab, offset: number, limit: number) => {
			if (authorId == null) return { items: [] as IAuthorBlogItem[], total: 0 };
			const res = await publicAxios.get(`/author/public/${authorId}/blogs`, {
				params: { sort: nextTab, offset, limit },
			});
			const payload = res.data?.data ?? res.data;
			return {
				items: Array.isArray(payload?.items)
					? (payload.items as IAuthorBlogItem[])
					: [],
				total: typeof payload?.total === "number" ? payload.total : 0,
			};
		},
		[authorId],
	);

	const seededItems: IAuthorBlogItem[] = useMemo(
		() => (Array.isArray(seededBlogs?.items) ? seededBlogs.items : []),
		[seededBlogs],
	);
	const seededTotal =
		typeof seededBlogs?.total === "number"
			? seededBlogs.total
			: seededItems.length;
	/**
	 * Content signature of the seeded batch, so the effect below re-seeds when the
	 * blogs actually change — not every time the parent hands down a fresh object
	 * with identical contents. Depending on the object itself meant any unrelated
	 * re-render wiped a loaded second batch off the screen mid-scroll.
	 */
	const seedSignature = `${seededTotal}|${seededItems.map((b) => b.id).join(",")}`;

	// First page per tab. "published" reuses the server-seeded batch so the initial
	// render needs no client request; other tabs / preview fall back to a fetch.
	useEffect(() => {
		if (authorId == null) {
			setBlogs({ items: [], total: 0, visible: PAGE_SIZE });
			return;
		}

		// The seed IS the first page (server sends exactly PAGE_SIZE), so slicing
		// guards against a seed that ever grows past one page.
		const seedWith = (items: IAuthorBlogItem[], total: number) =>
			setBlogs({
				items: items.slice(0, PAGE_SIZE),
				total,
				visible: PAGE_SIZE,
			});

		if (isCmsPreview) {
			seedWith(seededItems, seededTotal);
			return;
		}

		if (tab === "published" && seededItems.length) {
			seedWith(seededItems, seededTotal);
			return;
		}

		let cancelled = false;
		setLoading(true);
		fetchPage(tab, 0, PAGE_SIZE)
			.then((res) => {
				if (cancelled) return;
				seedWith(res.items, res.total);
			})
			.catch((e) => console.error(e))
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
		// seedSignature stands in for seededItems/seededTotal — see above.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authorId, tab, isCmsPreview, seedSignature, fetchPage]);

	const handleLoadMore = async () => {
		if (loading) return;

		// Anything already fetched but not yet revealed comes first.
		if (blogs.visible < blogs.items.length) {
			setBlogs((prev) => ({
				...prev,
				visible: Math.min(prev.visible + PAGE_SIZE, prev.items.length),
			}));
			return;
		}
		if (blogs.items.length >= blogs.total || isCmsPreview) return;

		setLoading(true);
		try {
			const next = await fetchPage(tab, blogs.items.length, PAGE_SIZE);
			setBlogs((prev) => {
				// Merge by id. A blog published between two requests shifts the
				// server's window, so a page can repeat a row the list already has —
				// and a repeated row means a repeated React key, which is what makes
				// a batch render as fewer cards than were fetched.
				const seen = new Set(prev.items.map((b) => b.id));
				const merged = [...prev.items];
				for (const item of next.items) {
					if (seen.has(item.id)) continue;
					seen.add(item.id);
					merged.push(item);
				}
				return {
					items: merged,
					total: next.total || prev.total,
					visible: Math.min(prev.visible + PAGE_SIZE, merged.length),
				};
			});
		} catch (e) {
			console.error(e);
		} finally {
			setLoading(false);
		}
	};

	if (!sectionData || !isTruthy(sectionData.status)) return null;
	// No author selected (or the author was unpublished) → the widget has nothing to show.
	if (!author) return null;

	const {
		font_family = "var(--font-poppins), Poppins, sans-serif",
		background_color = "#FFFFFF",
		use_gradient_background,
		gradient_angle = "34deg",
		gradient_start_color = "rgba(95, 78, 226, 0.1)",
		gradient_end_color = "rgba(0, 212, 235, 0.1)",
		show_breadcrumb,
		breadcrumb_text_color = "rgba(0,0,0,0.6)",
		breadcrumb_active_color = "#342B7C",
		card_background_color = "#FFFFFF",
		card_border_color = "rgba(0,0,0,0.1)",
		author_name_color = "#342B7C",
		author_role_color = "rgba(0,0,0,0.6)",
		badge_text_color = "rgba(0,0,0,0.6)",
		badge_border_color = "rgba(0,0,0,0.2)",
		badge_background_color = "#FFFFFF",
		top_contributor_label = "Top Contributor",
		top_contributor_icon_url = "",
		author_of_month_label = "Author of the Month",
		author_of_month_icon_url = "",
		associated_prefix_label = "Associated with upGrad since",
		associated_label_color = "rgba(0,0,0,0.5)",
		associated_value_color = "#000000",
		divider_color = "rgba(0,0,0,0.1)",
		domain_label = "Domain:",
		current_role_label = "Current role in the industry :",
		education_label = "Education Qualification :",
		expertise_label = "Expertise :",
		tools_label = "Tools & Technologies :",
		certifications_label = "Certifications :",
		articles_label = "of articles published",
		articles_count_suffix = "+",
		info_label_color = "rgba(0,0,0,0.6)",
		info_value_color = "#000000",
		chip_background_color = "rgba(95, 78, 226, 0.1)",
		chip_text_color = "#000000",
		about_title = "About",
		about_title_color = "#342B7C",
		about_description_color = "rgba(0,0,0,0.8)",
		follow_label = "Follow me on :",
		follow_label_color = "#000000",
		social_icon_background_color = "#FFFFFF",
		linkedin_icon_url = "",
		quora_icon_url = "",
		twitter_icon_url = "",
		blog_icon_url = "",
		show_blog_list,
		blog_list_background_color = "#FFFFFF",
		published_tab_label = "Published",
		popular_tab_label = "Most Popular",
		tab_text_color = "rgba(0,0,0,0.6)",
		tab_active_text_color = "#342B7C",
		tab_indicator_color = "#342B7C",
		blog_card_background_color = "#FFFFFF",
		blog_card_border_color = "rgba(0,0,0,0.1)",
		blog_card_title_color = "#101112",
		blog_card_date_color = "rgba(0,0,0,0.6)",
		blog_card_date_icon_url = "",
		read_more_label = "Read More",
		read_more_text_color = "#342B7C",
		load_more_label = "Load More",
		load_more_text_color = "#342B7C",
		load_more_background_color = "#FFFFFF",
		load_more_border_color = "#342B7C",
	} = sectionData;

	const heroBackground = isTruthy(use_gradient_background)
		? {
			backgroundImage: `linear-gradient(${gradient_angle || "34deg"}, ${gradient_start_color} 0%, ${gradient_end_color} 100%)`,
		}
		: { backgroundColor: background_color || "#FFFFFF" };

	const badges = [
		isTruthy(author.is_top_contributor) && {
			key: "top",
			label: top_contributor_label,
			iconUrl: top_contributor_icon_url || DEFAULT_BADGE_ICONS.top,
		},
		isTruthy(author.is_author_of_month) && {
			key: "month",
			label: author_of_month_label,
			iconUrl: author_of_month_icon_url || DEFAULT_BADGE_ICONS.month,
		},
		isTruthy(author.custom_badge_enabled) &&
		!!author.custom_badge_label && {
			key: "custom",
			label: author.custom_badge_label,
			iconUrl: author.custom_badge_icon_url,
		},
	].filter(Boolean) as {
		key: string;
		label: string;
		iconUrl?: string | null;
	}[];

	const socials = [
		author.linkedin_url && {
			key: "linkedin",
			href: author.linkedin_url,
			iconUrl: linkedin_icon_url || DEFAULT_SOCIAL_ICONS.linkedin,
			label: "LinkedIn",
		},
		author.quora_url && {
			key: "quora",
			href: author.quora_url,
			iconUrl: quora_icon_url || DEFAULT_SOCIAL_ICONS.quora,
			label: "Quora",
		},
		author.twitter_url && {
			key: "twitter",
			href: author.twitter_url,
			iconUrl: twitter_icon_url || DEFAULT_SOCIAL_ICONS.twitter,
			label: "Twitter",
		},
		author.author_blog_url && {
			key: "blog",
			href: author.author_blog_url,
			iconUrl: blog_icon_url,
			label: "Blog",
		},
	].filter(Boolean) as {
		key: string;
		href: string;
		iconUrl?: string | null;
		label: string;
	}[];

	const crumbs = Array.isArray(sectionData?.metadata)
		? [...sectionData.metadata]
			.filter((c: any) => isTruthy(c?.meta_status ?? true))
			.filter((c: any) => String(c?.meta_label ?? "").trim())
			.sort(
				(a: any, b: any) =>
					(Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
			)
		: [];

	const showBreadcrumb = isTruthy(show_breadcrumb) && crumbs.length > 0;

	const associatedDate = formatAssociatedDate(author.associated_date);
	const articleCount = author.articles_published ?? 0;
	const expertise = splitList(author.expertise);
	const tools = splitList(author.tools_technologies);
	const certifications = splitList(author.certifications);
	// Never more than `visible` cards, and never a Load More with nothing behind it.
	const visiblePosts = blogs.items.slice(0, blogs.visible);
	const showLoadMore =
		visiblePosts.length < Math.max(blogs.total, blogs.items.length);
	const showBlogList = isTruthy(show_blog_list);

	// Desktop (sm = 1080px and up): the About column ends exactly where the gradient band does, so the
	// blog tabs always begin on the white below it. Letting the column size to its text
	// made a long bio shove "Published / Most Popular" far down the page, and the tabs
	// landed in a different place for every author. Height = band − everything stacked
	// above the column inside it (top padding + breadcrumb row + its margin).
	const HERO_BAND_HEIGHT_DESKTOP = 360;
	const aboutColumnHeightDesktop =
		HERO_BAND_HEIGHT_DESKTOP - (showBreadcrumb ? 24 + 21 + 26 : 70);

	return (
		<div
			className="relative isolate w-full"
			style={{
				fontFamily: font_family,
				// Hero colour only. The blog list paints its own band below — putting the
				// blog colour here tinted the hero too, because the gradient is built from
				// translucent stops and let whatever sat underneath show through.
				backgroundColor: background_color,
			}}
		>
			{/* Fixed-height gradient band (Figma: 315px mobile / 360px desktop). Content
          flows over it, so a tall profile card naturally overflows into the plain
          background below — exactly as designed, with no measurement needed. */}
			<div
				aria-hidden
				className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[315px] sm:h-[360px]"
				style={heroBackground}
			/>

			{/* Blog list band: everything below the hero, so "Blog List Background" only
          repaints the blog area and leaves the gradient untouched. */}
			{showBlogList ? (
				<div
					aria-hidden
					className="pointer-events-none absolute inset-x-0 bottom-0 top-[315px] -z-10 sm:top-[360px]"
					style={{ backgroundColor: blog_list_background_color }}
				/>
			) : null}

			{/* The card's avatar overhangs its top edge (40px mobile / 24px desktop), so
          the breadcrumb row is what normally gives it clearance. With no crumbs the
          top padding has to stand in, otherwise the avatar sits flush against the
          section edge. */}
			<div
				className={cn(
					"mx-auto w-full max-w-[1750px] px-6 pb-10 tab:px-[60px] sm:px-[80px] sm:pb-14",
					showBreadcrumb ? "pt-6" : "pt-12 sm:pt-[70px]",
				)}
			>
				{showBreadcrumb ? (
					<nav
						aria-label="Breadcrumb"
						className="mb-8 flex flex-wrap items-center gap-2 text-[12px] sm:mb-[26px] sm:text-[14px]"
					>
						{crumbs.map((crumb, i) => {
							const href = String(crumb.meta_url ?? "").trim();
							// The last crumb IS the current page, so it wears the active colour
							// whether or not an admin also gave it a URL. Keying that off a blank
							// URL alone meant a filled-in last crumb silently rendered as a plain
							// link and the "Breadcrumb Current Page Color" setting did nothing.
							// A lone crumb that links somewhere is just that link (usually
							// "Home"), not the page you are on.
							const isCurrent =
								i === crumbs.length - 1 && (crumbs.length > 1 || !href);
							const color = isCurrent
								? breadcrumb_active_color
								: breadcrumb_text_color;
							return (
								<Fragment key={crumb.id ?? `${crumb.meta_label}-${i}`}>
									{i > 0 ? (
										<ChevronRight
											className="size-[11px] shrink-0"
											aria-hidden
											style={{ color: breadcrumb_text_color }}
										/>
									) : null}
									{href ? (
										<Link
											href={href}
											aria-current={isCurrent ? "page" : undefined}
											className={cn(
												"transition-opacity hover:opacity-80",
												isCurrent && "font-medium",
											)}
											style={{ color }}
										>
											{crumb.meta_label}
										</Link>
									) : (
										<span
											aria-current={isCurrent ? "page" : undefined}
											className={cn(isCurrent && "font-medium")}
											style={{ color }}
										>
											{crumb.meta_label}
										</span>
									)}
								</Fragment>
							);
						})}
					</nav>
				) : null}

				{/* Desktop: card in a fixed left column spanning both rows (it overflows the
            band), About top-right, blog list bottom-right. Mobile: plain stack.
            minmax(0,1fr) rather than 1fr: a blog card with a long unbroken title
            would otherwise widen the whole track and drag the tab row out with it.
            Row gap is Figma's 20px — 48px left a visible hole above the tabs.

            Row 1 is pinned to the hero band's height. It has to be the TRACK, not
            just the About column inside it: the card spans both rows, and grid
            spreads a spanning item's excess height over every auto track it covers,
            so a tall card silently inflated row 1 and carried the tabs down with it.
            Pinning row 1 sends all of that overflow into row 2, where the card is
            meant to hang over the blog list. */}
				<div
					className={cn(
						"flex flex-col gap-10 sm:grid sm:grid-cols-[400px_minmax(0,1fr)] sm:items-start sm:gap-x-10 sm:gap-y-5 lg:grid-cols-[460px_minmax(0,1fr)]",
						showBlogList && "sm:grid-rows-[var(--author-about-height)_auto]",
					)}
					style={
						{
							"--author-about-height": `${aboutColumnHeightDesktop}px`,
						} as React.CSSProperties
					}
				>
					{/* Profile card — avatar overlaps the top-right corner (Figma). */}
					<div
						className="relative mt-10 w-full rounded-[20px] border border-solid px-4 py-5 shadow-[0px_2px_5px_rgba(52,43,124,0.1)] sm:row-span-2 sm:mt-0 sm:self-start sm:p-6"
						style={{
							backgroundColor: card_background_color,
							borderColor: card_border_color,
						}}
					>
						{author.profile_image_url ? (
							// eslint-disable-next-line @next/next/no-img-element
							<img
								src={author.profile_image_url}
								alt={author.name}
								className="absolute right-4 top-0 size-20 -translate-y-1/2 rounded-[24px] border-2 border-solid border-white object-cover shadow-[0px_2px_14px_0px_rgba(52,43,124,0.15)] sm:right-6 sm:size-[100px] sm:-translate-y-6"
							/>
						) : null}

						<div className="flex flex-col gap-[13px] sm:gap-4">
							<div className="flex flex-col gap-2 [word-break:break-word]">
								{/* Name + tagline are a tight pair (no gap) in the design. */}
								<div className="flex flex-col font-semibold leading-normal">
									<p
										className="pr-24 text-[24px] sm:pr-28"
										style={{ color: author_name_color }}
									>
										{author.name}
									</p>
									{author.tagline ? (
										<p className="text-[12px]" style={{ color: author_role_color }}>
											{author.tagline}
										</p>
									) : null}
								</div>
								<p
									className="text-[12px] leading-normal"
									style={{ color: author_role_color }}
								>
									<span className="font-semibold">
										{articleCount}
										{articles_count_suffix ?? ""}
									</span>
									{articles_label ? ` ${articles_label}` : ""}
								</p>
							</div>

							{badges.length ? (
								<div className="flex flex-wrap content-start items-start gap-2.5">
									{badges.map((badge) => (
										<Badge
											key={badge.key}
											label={badge.label}
											iconUrl={badge.iconUrl}
											textColor={badge_text_color}
											borderColor={badge_border_color}
											backgroundColor={badge_background_color}
										/>
									))}
								</div>
							) : null}

							{associatedDate ? (
								<p
									className="text-[12px] leading-[1.4] tracking-[0.12px] [word-break:break-word]"
									style={{ color: associated_label_color }}
								>
									{associated_prefix_label}{" "}
									<span
										className="font-semibold"
										style={{ color: associated_value_color }}
									>
										{associatedDate}
									</span>
								</p>
							) : null}
						</div>

						<hr
							className="my-5 border-0 border-t sm:my-7"
							style={{ borderColor: divider_color }}
						/>

						<div className="flex flex-col gap-[18px] leading-[1.49] sm:gap-6">
							<InfoRow
								label={domain_label}
								value={author.domain}
								labelColor={info_label_color}
								valueColor={info_value_color}
							/>
							<InfoRow
								label={current_role_label}
								value={author.current_role}
								labelColor={info_label_color}
								valueColor={info_value_color}
							/>
							<InfoRow
								label={education_label}
								value={author.educational_qualification}
								labelColor={info_label_color}
								valueColor={info_value_color}
							/>
							<ChipGroup
								label={expertise_label}
								items={expertise}
								labelColor={info_label_color}
								chipBackgroundColor={chip_background_color}
								chipTextColor={chip_text_color}
							/>
							<ChipGroup
								label={tools_label}
								items={tools}
								labelColor={info_label_color}
								chipBackgroundColor={chip_background_color}
								chipTextColor={chip_text_color}
							/>
							<ChipGroup
								label={certifications_label}
								items={certifications}
								labelColor={info_label_color}
								chipBackgroundColor={chip_background_color}
								chipTextColor={chip_text_color}
							/>
						</div>
					</div>

					{/* About column. With a blog list below it, the column is pinned to the
              band height so the tabs never move; the bio scrolls inside it instead
              of pushing them, and "Follow me on" stays on the bottom edge (Figma). */}
					<div
						className={cn(
							"flex min-w-0 flex-col gap-5",
							// Fills the pinned row, so the bio can scroll and "Follow me on"
							// can sit on the band's bottom edge.
							showBlogList && "sm:h-full sm:self-stretch sm:pb-6",
						)}
					>
						<p
							className="text-[20px] font-semibold leading-[1.49] [word-break:break-word] sm:text-[24px]"
							style={{ color: about_title_color }}
						>
							{about_title}
						</p>
						{author.description ? (
							<p
								className={cn(
									"whitespace-pre-line text-[14px] leading-[1.49] [word-break:break-word] sm:text-[16px]",
									// Desktop only: 6 lines, so a long bio can never reach the tabs.
									// Mobile is a plain stack with nothing below to protect, so it
									// keeps the whole text. Clamped text stays in the DOM either way.
									showBlogList && "sm:line-clamp-6",
								)}
								style={{ color: about_description_color }}
							>
								{author.description}
							</p>
						) : null}
						{socials.length ? (
							<div
								className={cn(
									"flex flex-wrap items-center gap-2.5",
									showBlogList && "sm:mt-auto",
								)}
							>
								<p
									className="text-[14px] font-medium leading-normal sm:text-[16px]"
									style={{ color: follow_label_color }}
								>
									{follow_label}
								</p>
								{socials.map((social) => (
									<SocialLink
										key={social.key}
										href={social.href}
										iconUrl={social.iconUrl}
										label={social.label}
										backgroundColor={social_icon_background_color}
									/>
								))}
							</div>
						) : null}
					</div>

					{/* Author's blogs: tabs + cards + load more */}
					{showBlogList ? (
						<div className="flex min-w-0 flex-col">
							<div
								className="mb-6 flex items-stretch border-b"
								style={{ borderColor: card_border_color }}
								role="tablist"
							>
								{BLOG_TABS.map(({ key, labelKey }) => {
									const active = tab === key;
									const label =
										labelKey === "published"
											? published_tab_label
											: popular_tab_label;
									return (
										<button
											key={key}
											type="button"
											role="tab"
											aria-selected={active}
											onClick={() => setTab(key)}
											className={cn(
												// nowrap: Figma keeps each label on one line — wrapping made
												// the row two lines tall as soon as the track got narrower.
												"flex-1 whitespace-nowrap border-b-2 px-4 py-2.5 text-[16px] transition-colors sm:flex-none sm:text-[18px]",
												active ? "font-semibold" : "font-normal",
											)}
											style={{
												color: active ? tab_active_text_color : tab_text_color,
												borderColor: active
													? tab_indicator_color
													: "transparent",
											}}
										>
											{label}
										</button>
									);
								})}
							</div>

							{visiblePosts.length === 0 ? (
								<p
									className="py-8 text-center text-sm"
									style={{ color: tab_text_color }}
								>
									{loading
										? "Loading…"
										: tab === "popular"
											? "No popular blogs yet."
											: "No blogs published yet."}
								</p>
							) : (
								// Figma 13128:24163 — three equal columns (grid-cols-3 is
								// repeat(3,minmax(0,1fr)), so a long title wraps instead of widening
								// its track). 3-up switches at `sm`, the same breakpoint as the
								// two-column desktop layout, so the row count can never disagree with
								// the layout around it. NOTE the project's own scale (globals.css):
								// tab = 768, sm = 1080, md = 1230, lg = 1430 — nothing like Tailwind's
								// defaults, and mixing the two is what produced 2-up desktop rows.
								// Single-column range: Figma's 327px card (13143:14685), centred,
								// rather than one card stretched across a 700px phone/tablet.
								// `w-full` is load-bearing: this is a flex child, and `mx-auto`
								// without a definite width cancels the stretch and shrinks the grid
								// to fit-content — which, with size-contained (@container) cards
								// contributing no intrinsic width, collapsed every track to 18px.
								<div className="mx-auto grid w-full max-w-[327px] grid-cols-1 gap-[14px] tab:max-w-none tab:grid-cols-2 sm:grid-cols-3">
									{visiblePosts.map((post) => {
										const dateStr = formatBlogDate(post.created_at);
										return (
											<article
												key={post.id}
												// min-w-0: a long unbroken title must wrap inside its track
												// rather than stretch the grid and the tab row above it.
												// @container: the text below sizes off the CARD's width, not
												// the viewport's — three cards in a 1080px layout are half the
												// width they are at 1440px, and Figma's flat 14px looked
												// oversized in the narrow one.
												className="@container flex min-h-0 min-w-0 flex-col gap-3 rounded-lg border border-solid p-2 transition-shadow hover:shadow-md"
												style={{
													backgroundColor: blog_card_background_color,
													borderColor: blog_card_border_color,
												}}
											>
												<Link
													href={post.path || "/"}
													// Figma: 311×160 on mobile (13143:14686), 231×100 in the
													// desktop 3-up. One flat aspect ratio squashed the mobile
													// banner to a letterbox as the card grew.
													className="relative block aspect-311/160 w-full overflow-hidden rounded-lg bg-neutral-200 sm:aspect-231/100"
												>
													{post.blog_banner_image ? (
														// eslint-disable-next-line @next/next/no-img-element
														<img
															src={post.blog_banner_image}
															alt=""
															className="absolute inset-0 size-full object-cover"
														/>
													) : null}
												</Link>

												<div className="flex min-h-0 flex-1 flex-col gap-3 px-1 pb-2">
													{dateStr ? (
														<div className="flex items-center gap-1.5">
															{blog_card_date_icon_url ? (
																// eslint-disable-next-line @next/next/no-img-element
																<img
																	src={blog_card_date_icon_url}
																	alt=""
																	className="size-4 shrink-0 object-contain"
																/>
															) : (
																<Calendar
																	className="size-4 shrink-0"
																	strokeWidth={1.75}
																	aria-hidden
																	style={{ color: blog_card_date_color }}
																/>
															)}
															<span
																// Figma's 12px is the CAP, reached once the card is at
																// its designed width; below that it scales with the card.
																className="whitespace-nowrap text-[clamp(10px,4.8cqw,12px)] leading-[1.5]"
																style={{ color: blog_card_date_color }}
															>
																{dateStr}
															</span>
														</div>
													) : null}

													<h3
														className="line-clamp-3 text-[clamp(12px,5.6cqw,14px)] font-semibold leading-[1.5]"
														style={{ color: blog_card_title_color }}
													>
														<Link href={post.path || "/"}>
															{post.blog_title || ""}
														</Link>
													</h3>

													<Link
														href={post.path || "/"}
														className="mt-auto inline-flex items-center gap-1.5 transition-opacity hover:opacity-90"
														style={{ color: read_more_text_color }}
													>
														<span className="text-[14px] font-semibold leading-tight">
															{read_more_label}
														</span>
														<BlogCardReadMoreArrow className="size-[13px]" />
													</Link>
												</div>
											</article>
										);
									})}
								</div>
							)}

							{showLoadMore ? (
								<div className="mt-8 flex justify-center">
									<button
										type="button"
										disabled={loading}
										onClick={handleLoadMore}
										className="rounded-lg border border-solid px-5 py-3 text-[14px] font-semibold transition-opacity disabled:opacity-60"
										style={{
											color: load_more_text_color,
											backgroundColor: load_more_background_color,
											borderColor: load_more_border_color,
										}}
									>
										{loading ? "Loading…" : load_more_label}
									</button>
								</div>
							) : null}
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
}
